import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { useOnboarding } from '../../../../context/OnboardingContext';
import { PrivateWizardLayout, WizardContainer, WizardCard, WizardActions } from '../../WizardLayout';
import Stepper from '../../Stepper';
import { toast } from 'react-toast';
import '../../../../styles/wizard.css';

const StepM5ConversionSettings = () => {
  const navigate = useNavigate();
  const { migrate, setMigrate, nextStep, previousStep } = useOnboarding();
  
  const [settings, setSettings] = useState({
    runMode: migrate.runMode || 'as_is',
    wrapperEnabled: migrate.wrapperEnabled !== undefined ? migrate.wrapperEnabled : true,
    autoDeps: migrate.autoDeps !== undefined ? migrate.autoDeps : true,
    convertToPlaywright: migrate.convertToPlaywright || false
  });

  const [envVars, setEnvVars] = useState(migrate.envVars || {});
  const [newEnvKey, setNewEnvKey] = useState('');
  const [newEnvValue, setNewEnvValue] = useState('');

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleAddEnvVar = () => {
    if (!newEnvKey) {
      toast.error('Please enter a variable name');
      return;
    }
    
    setEnvVars(prev => ({
      ...prev,
      [newEnvKey]: newEnvValue
    }));
    
    setNewEnvKey('');
    setNewEnvValue('');
    toast.success('Environment variable added');
  };

  const handleRemoveEnvVar = (key) => {
    setEnvVars(prev => {
      const newVars = { ...prev };
      delete newVars[key];
      return newVars;
    });
  };

  const handleContinue = () => {
    // Save settings
    setMigrate({
      ...settings,
      envVars
    });

    nextStep();
    navigate('/app/onboarding?step=6');
  };

  return (
    <PrivateWizardLayout>
      <WizardContainer>
        <Stepper currentStep={5} completedSteps={[1, 2, 3, 4]} />
        
        <WizardCard
          title="Standardize in Robitest"
          subtitle="Configure how your imported tests will run in Robitest platform"
        >
          {/* Info Box - Execution Mode */}
          <div style={{
            padding: '1rem 1.25rem',
            background: 'rgba(76, 201, 240, 0.05)',
            border: '1px solid rgba(76, 201, 240, 0.2)',
            borderRadius: '8px',
            marginBottom: '1.5rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '1.25rem' }}>ℹ️</span>
              <div>
                <strong style={{ fontSize: '0.9375rem' }}>Execution Mode:</strong>{' '}
                <span style={{ fontSize: '0.9375rem', color: 'var(--text-secondary)' }}>
                  Run as-is in current framework ({migrate.frameworkDetected})
                </span>
              </div>
            </div>
          </div>

          {/* Robitest Enhancements - Compact Checkboxes */}
          <div style={{
            padding: '1.25rem',
            background: 'white',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            marginBottom: '1.5rem'
          }}>
            <h4 style={{ fontSize: '0.9375rem', fontWeight: '600', marginBottom: '1rem' }}>
              Robitest Enhancements
            </h4>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={settings.wrapperEnabled}
                  onChange={(e) => handleSettingChange('wrapperEnabled', e.target.checked)}
                  style={{ marginTop: '0.125rem', width: '18px', height: '18px' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '500', fontSize: '0.9375rem', marginBottom: '0.125rem' }}>
                    Enhanced Reporting Wrapper
                  </div>
                  <small style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem' }}>
                    Adds screenshots, logs, and detailed execution reports
                  </small>
                </div>
              </label>

              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={settings.autoDeps}
                  onChange={(e) => handleSettingChange('autoDeps', e.target.checked)}
                  style={{ marginTop: '0.125rem', width: '18px', height: '18px' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '500', fontSize: '0.9375rem', marginBottom: '0.125rem' }}>
                    Auto-install Dependencies
                  </div>
                  <small style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem' }}>
                    Install packages from package.json, requirements.txt, etc.
                  </small>
                </div>
              </label>
            </div>
          </div>

          {/* Environment Variables - Cleaner Section */}
          <div style={{
            padding: '1.25rem',
            background: 'white',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            marginBottom: '1.5rem'
          }}>
            <h4 style={{ fontSize: '0.9375rem', fontWeight: '600', marginBottom: '1rem' }}>
              Environment Variables (Optional)
            </h4>
            
            {/* Existing env vars */}
            {Object.keys(envVars).length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                {Object.entries(envVars).map(([key, value]) => (
                  <div
                    key={key}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.625rem 0.75rem',
                      background: 'var(--bg-gray-50)',
                      borderRadius: '4px',
                      marginBottom: '0.5rem'
                    }}
                  >
                    <code style={{ flex: 1, fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                      {key}={value}
                    </code>
                    <button
                      type="button"
                      onClick={() => handleRemoveEnvVar(key)}
                      style={{
                        padding: '0.25rem 0.625rem',
                        background: 'transparent',
                        color: 'var(--danger-color)',
                        border: '1px solid var(--danger-color)',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        fontWeight: '500'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add new env var - Compact */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: '0.5rem', alignItems: 'center' }}>
              <input
                type="text"
                value={newEnvKey}
                onChange={(e) => setNewEnvKey(e.target.value)}
                placeholder="KEY"
                className="form-control"
                style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
              />
              <input
                type="text"
                value={newEnvValue}
                onChange={(e) => setNewEnvValue(e.target.value)}
                placeholder="value"
                className="form-control"
                style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
              />
              <button
                type="button"
                onClick={handleAddEnvVar}
                className="wizard-btn wizard-btn-secondary"
                disabled={!newEnvKey}
                style={{ padding: '0.625rem 1.25rem' }}
              >
                Add
              </button>
            </div>
          </div>

          <WizardActions
            showBack={true}
            onBack={() => {
              previousStep();
              navigate('/app/onboarding?step=4');
            }}
            showNext={true}
            onNext={handleContinue}
            nextLabel="Continue"
          />
        </WizardCard>
      </WizardContainer>
    </PrivateWizardLayout>
  );
};

export default StepM5ConversionSettings;